import React, { useState, useMemo, useEffect } from 'react';

import teacherService from '../../services/api/teacher';
import studentService from '../../services/api/student';

import { useQuery, useMutation, useQueryClient } from 'react-query';

import { useNavigate } from 'react-router-dom';

import { useToast } from '../../components/common/Toast';

import { useConfirm } from '../../hooks/useConfirm';

import { useCoursesSearch } from '../../hooks/useSearch';

import { 

  BookOpen, 

  Plus, 

  Search, 

  LayoutDashboard,

  Sparkles,

  UserCheck,

  Users,

  ChevronDown,

  Filter

} from 'lucide-react';

import LoadingSpinner from '../../components/common/LoadingSpinner';

import TeacherCourseCard from '../../components/cards/TeacherCourseCard';

import {

  getLessonDuration,

  formatDurationDisplay

} from '../../utils/videoUtils';



const TeacherCourses = () => {

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const toast = useToast();

  const { confirm, ConfirmDialogComponent } = useConfirm();

  const [currentPage, setCurrentPage] = useState(1);

  const [coursesPerPage] = useState(6);

  const [selectedSubject, setSelectedSubject] = useState('');

  const [selectedStage, setSelectedStage] = useState('');

  const [selectedGrade, setSelectedGrade] = useState('');

  const [showFilters, setShowFilters] = useState(false);

  // جلب بيانات الفلاتر الخاصة بالمدرس
  const { data: teacherSubjectsData } = useQuery(
    'teacher-subjects',
    () => teacherService.getSubjects(),
    {
      retry: false,
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  // Extract unique stages and grades from teacher's subjects
  const filters = useMemo(() => {
    // Check if teacherSubjectsData is directly an array or has subjects property
    const subjectsArray = Array.isArray(teacherSubjectsData) ? teacherSubjectsData : teacherSubjectsData?.subjects || [];
    
    if (!subjectsArray || subjectsArray.length === 0) {
      return { stages: [], subjects: [], grades: [] };
    }

    const stagesSet = new Set();
    const gradesSet = new Set();
    const stages = [];
    const grades = [];

    subjectsArray.forEach(subject => {
      // Add stage if not already added - use subject_id as stage_id since stage_id is undefined
      if (subject.subject_id && !stagesSet.has(subject.subject_id)) {
        stagesSet.add(subject.subject_id);
        stages.push({
          id: subject.subject_id,
          name: subject.stage_name
        });
      }

      // Add grade if not already added - use subject_id as stage_id for grade
      if (subject.grade_id && !gradesSet.has(subject.grade_id)) {
        gradesSet.add(subject.grade_id);
        grades.push({
          id: subject.grade_id,
          name: subject.grade_name,
          stage_id: subject.subject_id // Use subject_id as stage_id
        });
      }
    });

    return {
      stages: stages.sort((a, b) => a.name.localeCompare(b.name)),
      subjects: subjectsArray.map(s => ({
        id: s.teacher_subject_id,
        name: s.subject_name,
        grade_name: s.grade_name
      })).sort((a, b) => a.name.localeCompare(b.name)),
      grades: grades.sort((a, b) => a.name.localeCompare(b.name))
    };
  }, [teacherSubjectsData]);

  // Extract grades based on selected stage
  const allGrades = useMemo(() => {
    if (!selectedStage) return filters?.grades || [];
    return filters?.grades?.filter(grade => grade.stage_id === parseInt(selectedStage)) || [];
  }, [selectedStage, filters?.grades]);

  // دالة حساب إجمالي مدة الكورس (نفس منطق صفحة الطالب بالكامل)

  const calculateTotalCourseDuration = useMemo(() => {

    return (course) => {

      if (!course) return 0;

      

      // أولوية: استخدام total_duration_seconds من API (محسوبة من مجموع مدة الدروس)

      if (course.total_duration_seconds && parseInt(course.total_duration_seconds) > 0) {

        return parseInt(course.total_duration_seconds) / 60; // تحويل من ثواني لدقائق

      }

      

      // ثانوية: استخدام حقول المدة المباشرة من الكورس (duration_hours, duration_minutes)

      if (course.duration_hours || course.duration_minutes) {

        const hours = parseInt(course.duration_hours) || 0;

        const minutes = parseInt(course.duration_minutes) || 0;

        return (hours * 60) + minutes; // تحويل لدقائق

      }

      

      // ثالثية: استخدام حقل duration_seconds إذا وجد

      if (course.duration_seconds) {

        return course.duration_seconds / 60; // تحويل من ثواني لدقائق

      }

      

      // رابعة: حساب من الدروس إذا وجدت

      if (course.videos && course.videos.length > 0) {

        const totalMinutes = course.videos.reduce((total, video) => {

          return total + (getLessonDuration(video) || 0);

        }, 0);

        return totalMinutes;

      }

      

      return 0;

    };

  }, []);



  // دالة لتنسيق عرض المدة الإجمالية - نفس المنطق من صفحة الطالب

  const formatTotalDuration = useMemo(() => {

    return (course) => {

      if (!course) return '--:--';

      const totalMinutes = calculateTotalCourseDuration(course);

      return formatDurationDisplay(totalMinutes * 60); // تحويل من دقائق للثواني

    };

  }, [calculateTotalCourseDuration]);



  // جلب إحصائيات المدرس

  const { data: teacherStats } = useQuery(

    'teacher-stats',

    () => teacherService.getTeacherStatistics()

  );



  // جلب بيانات الكورسات مع تمييز الـ query key
  const { data: coursesData, isLoading } = useQuery(
    ['teacher-courses', 'list'],
    () => teacherService.getTeacherCourses(),
    {
      retry: false,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      cacheTime: 300000 // 5 minutes cache
    }
  );

  const courses = useMemo(() => {
    // Handle different response structures from server
    if (!coursesData) {
      return [];
    }
    
    // The API returns { courses: [...], total, page, totalPages }
    if (coursesData.courses && Array.isArray(coursesData.courses)) {
      return coursesData.courses;
    }
    
    // If it's already an array
    if (Array.isArray(coursesData)) {
      return coursesData;
    }
    
    // If it has data property
    if (coursesData.data && Array.isArray(coursesData.data)) {
      return coursesData.data;
    }
    
    // If it's an object with course-like properties, convert to array
    if (typeof coursesData === 'object' && coursesData !== null) {
      return Object.values(coursesData).filter(item => 
        item && typeof item === 'object' && item.id && item.title
      );
    }
    
    return [];
  }, [coursesData]);

  // استخدام الـ Hook الجديد للبحث والفلترة
  const {
    searchTerm: hookSearchTerm,
    filteredData: searchedCourses,
    setSearchTerm: setHookSearchTerm,
    updateFilter
  } = useCoursesSearch(courses);

  // Debug info removed for production
  useEffect(() => {
    if (courses.length === 0 && coursesData) {
      // Courses array is empty but coursesData exists
    }
  }, [courses.length, coursesData]);

  // Handle search input change
  const handleSearch = (e) => {
    setHookSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Apply filters when they change
  useEffect(() => {
    // Apply subject filter - find subject by teacher_subject_id
    if (selectedSubject) {
      // selectedSubject is the teacher_subject_id, so filter by that
      updateFilter('teacher_subject_id', parseInt(selectedSubject));
    } else {
      updateFilter('teacher_subject_id', '');
    }
    
    // Apply stage filter - filter by stage_name from courses data
    if (selectedStage) {
      // Find stage name from filters
      const stageName = filters?.stages?.find(s => s.id.toString() === selectedStage)?.name;
      updateFilter('stage_name', stageName || '');
    } else {
      updateFilter('stage_name', '');
    }
    
    // Apply grade filter - filter by grade_name from courses data
    if (selectedGrade) {
      const gradeName = filters?.grades?.find(g => g.id.toString() === selectedGrade)?.name;
      updateFilter('grade_name', gradeName || '');
    } else {
      updateFilter('grade_name', '');
    }
  }, [selectedSubject, selectedStage, selectedGrade, updateFilter, filters]);

  // جلب الفيديوهات لكل كورس لعرض المعاينة (مع إصلاح الـ enabled condition)
  const { data: coursesWithVideos } = useQuery(
    ['courses-with-videos', courses?.length], // استخدام courses بدلاً من coursesData
    async () => {
      if (!courses || courses.length === 0) return [];
      
      const videosPromises = courses.map(async (course) => {
        try {
          const response = await teacherService.getCourseVideos(course.id);
          // response.data is already { videos: [...], total: X }
          const videos = response?.videos || response?.data?.videos || [];
          return { ...course, videos };
        } catch (error) {
          // Failed to fetch videos silently
          return { ...course, videos: [] };
        }
      });
      return Promise.all(videosPromises);
    },
    { 
      enabled: !!courses && courses.length > 0, 
      retry: false,
      staleTime: 60000 // 1 minute cache for videos
    }
  );

  // Fetch per-video analytics (views) for all videos in the loaded courses
  const [lessonAnalytics, setLessonAnalytics] = React.useState({});

  useEffect(() => {
    const allVideoIds = (coursesWithVideos || []).flatMap(c => (c.videos || []).map(v => v.id));
    if (!allVideoIds || allVideoIds.length === 0) return;

    const idsToFetch = allVideoIds.filter(id => !(id in lessonAnalytics));
    if (idsToFetch.length === 0) return;

    const fetchAll = async () => {
      const map = {};
      for (const id of idsToFetch) {
        try {
          const res = await studentService.getLessonAnalytics(id);
          map[id] = res?.data?.stats?.views ?? null;
        } catch (err) {
          map[id] = null;
        }
      }
      setLessonAnalytics(prev => ({ ...prev, ...map }));
    };

    fetchAll();
  }, [coursesWithVideos, lessonAnalytics]);

  // دمج البيانات المفلترة مع الفيديوهات (مع إصلاح الـ logic)
  const searchedCoursesWithVideos = useMemo(() => {
    if (!searchedCourses || searchedCourses.length === 0) return [];
    
    // إذا لم تحمل الفيديوهات بعد، استخدم searchedCourses مباشرة
    if (!coursesWithVideos || coursesWithVideos.length === 0) {
      return searchedCourses;
    }
    
    // إذا كانت الفيديوهات محملة، دمجها مع الكورسات المفلترة
    return searchedCourses.map(course => {
      const courseWithVideos = coursesWithVideos.find(c => c.id === course.id);
      return courseWithVideos || { ...course, videos: [] };
    });
  }, [searchedCourses, coursesWithVideos]);

  const deleteCourseMutation = useMutation(
    (courseId) => teacherService.deleteCourse(courseId),
    {
      onSuccess: (_data, deletedCourseId) => {
        toast.success('تم حذف الكورس بنجاح');
        // Invalidate main queries related to courses so UI refreshes
        queryClient.invalidateQueries('teacher-courses');
        queryClient.invalidateQueries('teacher-statistics');
        queryClient.invalidateQueries('courses-with-videos');

        // Optimistically remove deleted course from cached teacher-courses list
        try {
          queryClient.setQueryData(['teacher-courses', 'list'], (old) => {
            if (!old) return old;
            // old may be array or object with .courses or .data
            if (Array.isArray(old)) return old.filter(c => c.id !== deletedCourseId);
            if (old.courses && Array.isArray(old.courses)) return { ...old, courses: old.courses.filter(c => c.id !== deletedCourseId) };
            if (old.data && Array.isArray(old.data)) return { ...old, data: old.data.filter(c => c.id !== deletedCourseId) };
            return old;
          });
        } catch (err) {
          // ignore optimistic update errors
        }
      },
      onError: (error) => {
        toast.error('حدث خطأ أثناء محاولة الحذف');
      }
    }
  );

  const handleCourseClick = (course) => {
    navigate(`/teacher/courses/${course.id}`);
  };

  const handleEditCourse = (courseId) => {
    navigate(`/teacher/courses/${courseId}/edit`);
  };

  const handleDeleteCourse = async (courseId) => {
    const confirmed = await confirm({
      title: 'حذف الكورس',
      message: 'هل أنت متأكد أنك تريد حذف هذا الكورس؟ لا يمكن التراجع.',
      confirmText: 'حذف',
      cancelText: 'إلغاء'
    });

    if (confirmed) {
      deleteCourseMutation.mutate(courseId);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil((searchedCoursesWithVideos?.length || 0) / coursesPerPage);

  // Reset page when filters change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [hookSearchTerm, selectedSubject, selectedStage, selectedGrade]);


  if (isLoading && !coursesData) {

    return (

      <div className="flex flex-col items-center justify-center min-h-[400px]">

        <LoadingSpinner size="large" />

        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري جلب كورساتك...</p>

      </div>

    );

  }



  return (

    <div className="max-w-7xl mx-auto space-y-8 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">

      

      {/* Header Section */}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">

        <div className="space-y-1">

          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">

            <LayoutDashboard className="text-primary-600" />

            إدارة كورساتي

          </h1>

          <p className="text-gray-500 font-medium">تحكم في محتواك التعليمي، تابع طلابك، وطور دروسك.</p>

        </div>

        <button

          onClick={() => navigate('/teacher/create-course')}

          className="bg-primary-600 text-white px-8 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 transform active:scale-95"

        >

          <Plus size={20} /> إنشاء كورس جديد

        </button>

      </div>



      {/* Stats Cards Dashboard */}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

        {[

          { label: 'إجمالي الكورسات', value: teacherStats?.totalCourses || 0, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },

          { label: 'إجمالي الطلاب', value: teacherStats?.totalStudents || 0, icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50' },

          { label: 'إجمالي الاشتراكات', value: teacherStats?.totalActiveSubscriptions || 0, icon: UserCheck, color: 'text-orange-600', bg: 'bg-orange-50' },

          { label: 'كورسات نشطة', value: courses.filter(c => c.is_active === true).length, icon: Sparkles, color: 'text-purple-600', bg: 'bg-purple-50' }

        ].map((stat, i) => (

          <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">

            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>

              <stat.icon size={24} />

            </div>

            <div>

              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">{stat.label}</p>

              <p className="text-2xl font-black text-gray-900 mt-1">{stat.value}</p>

            </div>

          </div>

        ))}

      </div>



      {/* Search and Filters Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
        <div className="relative md:flex-1 group">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
          <input
            type="text"
            placeholder="ابحث عن كورس بالاسم أو الوصف..."
            value={hookSearchTerm}
            onChange={handleSearch}
            className="w-full pr-12 pl-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
          />
        </div>
        
        {/* Subject Filter Dropdown */}
        <div className="md:w-64">
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
          >
            <option value="">جميع المواد</option>
            {filters?.subjects?.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name} - {subject.grade_name}
              </option>
            ))}
          </select>
        </div>

        {/* Advanced Filters Button */}
        <div className="md:w-32">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full flex items-center justify-center gap-2 py-3 border rounded-xl font-bold transition-all ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-600' : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'}`}
          >
            <Filter size={18} />
            الفلاتر
            <ChevronDown size={18} className={`transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <div className="md:w-32">
          <div className="bg-primary-600 text-white py-3 rounded-xl text-center font-black text-xs uppercase tracking-widest shadow-lg shadow-primary-100">
              {searchedCoursesWithVideos?.length || 0} كورس
          </div>
        </div>
      </div>

      {/* Expandable Advanced Filters */}
      {showFilters && (
        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4
                        animate-in fade-in slide-in-from-top duration-300
                        relative z-50 overflow-visible">
          
          {/* المرحلة الدراسية */}
          <div className="space-y-2 relative overflow-visible">
              <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">المرحلة الدراسية</label>
              <div className="relative overflow-visible">
                  <select
                  value={selectedStage}
                  onChange={(e) => {
                    setSelectedStage(e.target.value);
                    setSelectedGrade(''); // Reset grade when stage changes
                  }}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                  >
                  <option value="">جميع المراحل</option>
                  {filters?.stages?.map((stage) => (
                      <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                  </select>
              </div>
          </div>

          {/* الصف الدراسي */}
          <div className="space-y-2 relative overflow-visible">
              <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الصف الدراسي</label>
              <div className="relative overflow-visible">
                  <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                  >
                  <option value="">جميع الصفوف</option>
                  {allGrades?.map((grade) => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
                  </select>
              </div>
          </div>
        </div>
      )}



      {/* Courses Grid Section */}
      {searchedCoursesWithVideos?.length === 0 ? (
        <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200">
          <div className="w-32 h-32 mx-auto mb-6">
            <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 rounded-[2.5rem] flex items-center justify-center shadow-sm">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 bg-primary-200 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <p className="text-xs text-primary-600 font-medium">كورس تعليمي</p>
              </div>
            </div>
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">{hookSearchTerm ? 'لا توجد نتائج لبحثك' : 'مكتبتك فارغة حالياً'}</h3>
          <p className="text-gray-500 max-w-sm mx-auto mb-8">
            {hookSearchTerm ? 'جرب البحث بكلمات أخرى أو قم بإعادة ضبط الفلاتر.' : 'ابدأ الآن بإنشاء أول كورس تعليمي لك وشاركه مع طلابك.'}
          </p>
          {!hookSearchTerm && (
            <button onClick={() => navigate('/teacher/create-course')} className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all">
              إنشاء أول كورس
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {searchedCoursesWithVideos.map((course) => {
            // sum analytics for this course's videos (robustly fall back to lesson.view fields)
            const vids = (course.videos || []);
            const courseViews = vids.reduce((sum, v) => {
              const stored = lessonAnalytics[v.id];
              let numeric = 0;

              if (stored != null) {
                if (typeof stored === 'object') {
                  numeric = parseInt(stored?.data?.stats?.views ?? stored?.data?.views ?? stored?.stats?.views ?? stored?.views ?? 0, 10) || 0;
                } else {
                  numeric = parseInt(stored, 10) || 0;
                }
              } else {
                // fallback to any views field present on the lesson object
                numeric = parseInt(v.views ?? v.video_views ?? v.views_count ?? 0, 10) || 0;
              }

              return sum + numeric;
            }, 0);
            return (
              <TeacherCourseCard 
                key={course.id}
                course={{ ...course, courseViews }}
                onCourseClick={handleCourseClick}
                onEditCourse={handleEditCourse}
                onDeleteCourse={handleDeleteCourse}
                isDeleting={deleteCourseMutation.isLoading}
                onNavigateVisuals={(courseId) => navigate(`/teacher/videos/${courseId}`)}
                formatTotalDuration={formatTotalDuration}
              />
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}

      {totalPages > 1 && (

        <div className="flex items-center justify-center gap-4 mt-8">

          <button

            onClick={() => handlePageChange(currentPage - 1)}

            disabled={currentPage === 1}

            className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"

          >

            السابق

          </button>

          

          <div className="flex items-center gap-2">

            {Array.from({ length: totalPages }, (_, index) => (

              <button

                key={index + 1}

                onClick={() => handlePageChange(index + 1)}

                className={`w-10 h-10 rounded-xl font-medium transition-all ${

                  currentPage === index + 1

                    ? 'bg-primary-600 text-white'

                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'

                }`}

              >

                {index + 1}

              </button>

            ))}

          </div>

          

          <button

            onClick={() => handlePageChange(currentPage + 1)}

            disabled={currentPage === totalPages}

            className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"

          >

            التالي

          </button>

        </div>

      )}

      

      {/* Confirmation Dialog Integration */}

      <ConfirmDialogComponent />

    </div>

  );

};



export default TeacherCourses;